import { useState, useEffect } from 'react';
import { useGetAllUsersSalariesQuery, useBulkSavePayDateMutation } from 'redux/features/salaryApi';
import { useGetTaxesQuery } from 'redux/features/taxApi';
import useUser from 'hooks/useUser';
import Loader from 'components/ui/Loader';
import Icon from 'components/AppIcon';
import dayjs from 'lib/dayjs';
import { toast } from 'react-toastify';
import SalaryBreakdownModal from '../../employee-time-logs/components/SalaryBreakdownModal';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const SalaryTab = () => {
  const currentUser = useUser();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [searchQuery, setSearchQuery] = useState('');

  // Breakdown modal state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showBreakdownModal, setShowBreakdownModal] = useState(false);
  const [modalViewMode, setModalViewMode] = useState('BREAKDOWN');

  // Toolbar pay date selection & selection states
  const [payDate, setPayDate] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSelectionControls, setShowSelectionControls] = useState(false);

  // API hooks
  const {
    data: salaryResponse,
    isLoading: isSalariesLoading,
    refetch: refetchSalaries,
  } = useGetAllUsersSalariesQuery(
    { month: selectedMonth, year: selectedYear },
    { refetchOnMountOrArgChange: true },
  );
  const { data: taxesData } = useGetTaxesQuery();
  const [bulkSavePayDate] = useBulkSavePayDateMutation();

  const months = MONTHS.filter((m) =>
    selectedYear === currentYear ? m.value <= currentMonth : selectedYear < currentYear,
  );
  const years = Array.from({ length: currentYear - 2010 + 1 }, (_, i) => currentYear - i);

  useEffect(() => {
    if (selectedYear === currentYear && selectedMonth > currentMonth) {
      setSelectedMonth(currentMonth);
    }
  }, [selectedYear, currentYear, currentMonth, selectedMonth]);

  const handlePreviousMonth = () => {
    let m = selectedMonth - 1,
      y = selectedYear;
    if (m < 1) {
      m = 12;
      y--;
    }
    if (y >= 2010) {
      setSelectedMonth(m);
      setSelectedYear(y);
    }
  };

  const handleNextMonth = () => {
    let m = selectedMonth + 1,
      y = selectedYear;
    if (m > 12) {
      m = 1;
      y++;
    }
    if (y < currentYear || (y === currentYear && m <= currentMonth)) {
      setSelectedMonth(m);
      setSelectedYear(y);
    }
  };

  const getRowDetails = (item) => {
    const isApproved = !!item.paySlip?.is_approved;
    const ds = isApproved ? item.paySlip : item.calculatedSalary || item.paySlip;

    const basicSalary = Number(ds?.basic_salary) || 0;
    const hra = Number(ds?.house_rent_allowance) || 0;
    const sa = Number(ds?.special_allowance) || 0;
    const oa = Number(ds?.other_allowance) || 0;

    const overtime = Number(ds?.overtime_salary) || 0;
    const paidLeave = Number(ds?.paid_leave_salary) || 0;
    const extraLeave = Number(ds?.extra_leave_salary) || 0;
    const otherLeave = Number(ds?.other_leave_salary) || 0;

    let additionsFromEntries = 0;
    let deductionsFromEntries = 0;

    if (item.paySlip?.additional_entries?.length > 0) {
      item.paySlip.additional_entries.forEach((entry) => {
        const amt = Number(entry.amount) || 0;
        if (entry.type === 'ADD' || entry.type === 'Add') {
          additionsFromEntries += amt;
        } else if (entry.type === 'SUBTRACT' || entry.type === 'Subtract') {
          deductionsFromEntries += amt;
        }
      });
    } else if (!item.paySlip) {
      const taxesList = taxesData || [];
      let taxEntries = [];
      if (Number(ds?.basic_salary) >= 15000) {
        taxEntries = taxesList.map((taxEntry) => {
          const amount =
            taxEntry.type === 'PERCENTAGE'
              ? (Number(ds?.total_salary) * Number(taxEntry.value)) / 100
              : Number(taxEntry.value);
          return {
            type: 'Subtract',
            amount: amount,
          };
        });
      }

      let incentiveEntries = [];
      if (item.user?.monthlyIncentives && item.user.monthlyIncentives.length > 0) {
        incentiveEntries = item.user.monthlyIncentives
          .filter(
            (incentive) => incentive.title && incentive.amount && Number(incentive.amount) > 0,
          )
          .map((incentive) => ({
            type: 'Add',
            amount: Number(incentive.amount),
          }));
      }

      const defaultEntries = [...taxEntries, ...incentiveEntries];
      defaultEntries.forEach((entry) => {
        if (entry.type === 'Add') {
          additionsFromEntries += entry.amount;
        } else {
          deductionsFromEntries += entry.amount;
        }
      });
    } else {
      let incentiveEntries = [];
      if (item.user?.monthlyIncentives && item.user.monthlyIncentives.length > 0) {
        incentiveEntries = item.user.monthlyIncentives
          .filter(
            (incentive) => incentive.title && incentive.amount && Number(incentive.amount) > 0,
          )
          .map((incentive) => ({
            type: 'Add',
            amount: Number(incentive.amount),
          }));
      }
      incentiveEntries.forEach((entry) => {
        additionsFromEntries += entry.amount;
      });
    }

    const totalAdditions = overtime + paidLeave + additionsFromEntries;
    const totalDeductions = extraLeave + otherLeave + deductionsFromEntries;
    const grossEarnings = basicSalary + hra + sa + oa + overtime + paidLeave + additionsFromEntries;
    const netPayUnrounded = grossEarnings - totalDeductions;
    const netPay = Math.round(netPayUnrounded);
    const roundingAdjustment = Number((netPay - netPayUnrounded).toFixed(2));

    return {
      basicSalary,
      hra,
      sa,
      oa,
      overtime,
      paidLeave,
      extraLeave,
      otherLeave,
      additionsFromEntries,
      deductionsFromEntries,
      totalAdditions,
      totalDeductions,
      grossEarnings,
      netPay,
      roundingAdjustment,
      ds,
      isApproved,
    };
  };

  const usersSalaries = salaryResponse?.data || [];
  const filteredUsersSalaries = usersSalaries.filter((item) => {
    const name = `${item.user?.first_name || ''} ${item.user?.last_name || ''}`.toLowerCase();
    const empId = (item.user?.employee_id || '').toLowerCase();
    const q = searchQuery.toLowerCase();
    return name.includes(q) || empId.includes(q);
  });

  // Calculate summary metrics
  let totalBudget = 0;
  let totalPaid = 0;
  let totalRemaining = 0;

  usersSalaries.forEach((item) => {
    if (Number(item.user?.salary?.basic_salary) > 0) {
      const details = getRowDetails(item);
      totalBudget += details.netPay;
      if (details.isApproved) {
        totalPaid += details.netPay;
      } else {
        totalRemaining += details.netPay;
      }
    }
  });

  // Only visible users with basic salary configured and whose payslip is NOT approved
  const eligibleUsers = filteredUsersSalaries.filter(
    (item) => Number(item.user?.salary?.basic_salary) > 0 && !item.paySlip?.is_approved,
  );

  const allSelected =
    eligibleUsers.length > 0 &&
    eligibleUsers.every((item) => selectedUserIds.includes(item.user.id));

  const toggleUser = (id) =>
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleAll = () => {
    if (allSelected) {
      setSelectedUserIds((prev) =>
        prev.filter((id) => !eligibleUsers.some((u) => u.user.id === id)),
      );
    } else {
      setSelectedUserIds((prev) => {
        const next = [...prev];
        eligibleUsers.forEach((u) => {
          if (!next.includes(u.user.id)) next.push(u.user.id);
        });
        return next;
      });
    }
  };

  const handleGenerateSlips = async () => {
    if (!selectedUserIds.length) {
      toast.error('Select at least one employee');
      return;
    }
    if (!payDate) {
      toast.error('Please select a Pay Date before saving.');
      return;
    }
    setIsGenerating(true);

    try {
      const res = await bulkSavePayDate({
        userIds: selectedUserIds,
        month: selectedMonth,
        year: selectedYear,
        payDate: new Date(payDate).toISOString(),
      }).unwrap();

      toast.success(`${res.data?.success || selectedUserIds.length} salary slip(s) saved as draft`);
    } catch {
      toast.error('Failed to save pay date slips');
    }

    setIsGenerating(false);
    refetchSalaries();
    setSelectedUserIds([]);
    setShowSelectionControls(false);
  };

  const handleCancel = () => {
    setSelectedUserIds([]);
    setShowSelectionControls(false);
  };

  const handleViewBreakdown = (employee, viewMode = 'BREAKDOWN') => {
    setSelectedEmployee(employee);
    setModalViewMode(viewMode);
    setShowBreakdownModal(true);
  };

  const handleDownloadExcel = () => {
    if (filteredUsersSalaries.length === 0) {
      toast.info('No salary records to export');
      return;
    }

    // CSV Headers
    const headers = [
      'Employee Name',
      'Pay Date',
      'Salary (Basic)',
      'Gross Earning',
      'Total Deduction',
      'Rounding Adjustment',
      'Total Net Pay',
      'Status',
    ];

    // CSV Rows
    const rows = filteredUsersSalaries.map((item) => {
      const details = getRowDetails(item);
      const name = `${item.user?.first_name || ''} ${item.user?.last_name || ''}`.trim();
      const payDateStr = details.ds?.calculated_at
        ? dayjs(details.ds.calculated_at).format('DD-MM-YYYY')
        : 'Not Provided';

      return [
        name,
        payDateStr,
        details.basicSalary,
        details.grossEarnings.toFixed(2),
        details.totalDeductions.toFixed(2),
        details.roundingAdjustment.toFixed(2),
        details.netPay,
        details.isApproved ? 'Paid' : 'Unpaid',
      ];
    });

    // Construct CSV Content
    const csvContent = [
      headers.join(','),
      ...rows.map((e) =>
        e
          .map((val) => {
            const valStr = String(val ?? '');
            if (valStr.includes(',') || valStr.includes('"') || valStr.includes('\n')) {
              return `"${valStr.replace(/"/g, '""')}"`;
            }
            return valStr;
          })
          .join(','),
      ),
    ].join('\n');

    // Create a blob and download it
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    const monthName = MONTHS.find((m) => m.value === selectedMonth)?.label || 'Month';
    link.setAttribute('href', url);
    link.setAttribute('download', `Salaries_${monthName}_${selectedYear}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fmt = (val) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(val || 0);

  return (
    <div className="flex flex-col h-[100] space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
        {/* Left: Search + Pay Date selection + Add Pay Date button */}
        <div className="flex flex-wrap items-center gap-4 min-w-0">
          {/* Search */}
          <div className="relative w-60">
            <input
              type="text"
              placeholder="Search employee..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 focus:bg-white
                         focus:ring-2 focus:ring-primary-500 transition-all text-sm"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="Search" size={15} className="text-gray-400" />
            </div>
          </div>

          {/* Pay Date picker & Action buttons */}
          {showSelectionControls ? (
            <>
              {/* Pay Date picker */}
              <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary-400 focus-within:border-primary-400 transition-all">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest whitespace-nowrap">
                  Pay Date:
                </label>
                <input
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  max={dayjs().format('YYYY-MM-DD')}
                  className="border-none bg-transparent p-0 text-sm text-gray-700 font-medium focus:ring-0 outline-none w-28 cursor-pointer"
                />
              </div>

              {/* Save button */}
              <button
                onClick={handleGenerateSlips}
                disabled={isGenerating || !selectedUserIds.length}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700
                           text-white font-bold text-sm rounded-xl transition-all shadow-sm
                           disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isGenerating ? (
                  <Icon name="Loader" size={15} className="animate-spin" />
                ) : (
                  <Icon name="Check" size={15} />
                )}
                Save${selectedUserIds.length ? ` (${selectedUserIds.length})` : ''}
              </button>

              {/* Cancel button */}
              <button
                onClick={handleCancel}
                disabled={isGenerating}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200
                           text-gray-700 font-bold text-sm rounded-xl transition-all shadow-sm
                           disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <Icon name="X" size={15} />
                Cancel
              </button>
            </>
          ) : (
            <>
              {/* Action button to enter selection mode */}
              <button
                onClick={() => setShowSelectionControls(true)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700
                           text-white font-bold text-sm rounded-xl transition-all shadow-sm whitespace-nowrap"
              >
                <Icon name="CalendarPlus" size={15} />
                Add Pay Date
              </button>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700
                           text-white font-bold text-sm rounded-xl transition-all shadow-sm whitespace-nowrap"
              >
                <Icon name="Download" size={15} />
                Download Excel
              </button>
            </>
          )}
        </div>

        {/* Month / Year nav */}
        <div className="flex items-center gap-2">
          <button
            onClick={handlePreviousMonth}
            disabled={selectedYear === 2010 && selectedMonth === 1}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Icon name="ChevronLeft" size={16} />
          </button>

          <div className="relative">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="appearance-none pl-3 pr-8 py-2 border border-gray-200 rounded-lg bg-white text-gray-700 font-medium text-sm min-w-[120px] focus:ring-2 focus:ring-primary-500"
            >
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="appearance-none pl-3 pr-8 py-2 border border-gray-200 rounded-lg bg-white text-gray-700 font-medium text-sm min-w-[90px] focus:ring-2 focus:ring-primary-500"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleNextMonth}
            disabled={selectedYear === currentYear && selectedMonth === currentMonth}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Icon name="ChevronRight" size={16} />
          </button>
        </div>
      </div>

      {/* Report Cards / Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Card 1: Total Salary Need to Pay */}
        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex items-center space-x-4">
          <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
            <Icon name="Banknote" size={24} />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Total Net Pay
            </p>
            <h3 className="text-2xl font-bold text-gray-900 mt-1">{fmt(totalBudget)}</h3>
            <p className="text-[10px] text-gray-400 mt-0.5">Total needed to pay</p>
          </div>
        </div>

        {/* Card 2: Total Paid (Approved) */}
        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex items-center space-x-4">
          <div className="p-3 bg-emerald-50 rounded-xl text-emerald-600">
            <Icon name="CheckCircle" size={24} />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Total Paid
            </p>
            <h3 className="text-2xl font-bold text-emerald-700 mt-1">{fmt(totalPaid)}</h3>
            <p className="text-[10px] text-gray-400 mt-0.5">Approved salary slips</p>
          </div>
        </div>

        {/* Card 3: Total Pending (Remaining) */}
        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex items-center space-x-4">
          <div className="p-3 bg-amber-50 rounded-xl text-amber-600">
            <Icon name="Clock" size={24} />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Remaining to Pay
            </p>
            <h3 className="text-2xl font-bold text-amber-700 mt-1">{fmt(totalRemaining)}</h3>
            <p className="text-[10px] text-gray-400 mt-0.5">Draft or pending slips</p>
          </div>
        </div>
      </div>

      {/* Salary table */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-auto min-h-[200px] sm:min-h-[300px] lg:min-h-[400px] max-h-[600px] lg:max-h-[800px] scrollbar-thin">
          {isSalariesLoading ? (
            <div className="py-20 flex justify-center">
              <Loader />
            </div>
          ) : filteredUsersSalaries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4 border border-gray-100">
                <Icon name="FileX" size={28} className="text-gray-300" />
              </div>
              <p className="text-sm font-bold text-gray-900 uppercase tracking-widest">
                No Salaries Found
              </p>
              <p className="text-xs text-gray-400 mt-1">
                No payroll data available for this period.
              </p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/70 border-b border-gray-200">
                  {showSelectionControls && (
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-gray-500 w-12 text-center">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                      />
                    </th>
                  )}
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-gray-500">
                    Employee
                  </th>
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-gray-500 text-right">
                    Basic
                  </th>
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-gray-500 text-right">
                    Additions
                  </th>
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-gray-500 text-right">
                    Deductions
                  </th>
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-gray-500 text-right">
                    Net Pay
                  </th>
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-gray-500 text-center">
                    Pay Date
                  </th>
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-gray-500 text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsersSalaries.map((item) => {
                  const details = getRowDetails(item);
                  const isCheckboxDisabled =
                    Number(item.user?.salary?.basic_salary) <= 0 || details.isApproved;
                  return (
                    <tr key={item.user?.id} className="hover:bg-gray-50/50 transition-colors">
                      {showSelectionControls && (
                        <td className="px-5 py-4 w-12 text-center">
                          <input
                            type="checkbox"
                            disabled={isCheckboxDisabled}
                            checked={selectedUserIds.includes(item.user?.id)}
                            onChange={() => toggleUser(item.user?.id)}
                            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                          />
                        </td>
                      )}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-bold text-sm">
                            {item.user?.first_name?.charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {item.user?.first_name} {item.user?.last_name}
                            </p>
                            <p className="text-xs text-gray-400">ID: {item.user?.employee_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right text-sm font-medium text-gray-700">
                        {fmt(details.basicSalary)}
                      </td>
                      <td className="px-5 py-4 text-right text-sm font-medium text-emerald-600">
                        +{fmt(details.totalAdditions)}
                      </td>
                      <td className="px-5 py-4 text-right text-sm font-medium text-rose-600">
                        -{fmt(details.totalDeductions)}
                      </td>
                      <td className="px-5 py-4 text-right text-sm font-bold text-gray-900">
                        {fmt(details.netPay)}
                      </td>
                      <td className="px-5 py-4 text-center">
                        {item.paySlip?.calculated_at ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border bg-emerald-50 text-emerald-700 border-emerald-200">
                            {dayjs(item.paySlip.calculated_at).format('DD-MM-YYYY')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border bg-gray-50 text-gray-700 border-gray-200">
                            Not Provided
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleViewBreakdown(item, 'BREAKDOWN')}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs rounded-lg transition-colors"
                          >
                            <Icon name="ClipboardList" size={13} />
                            Breakdown
                          </button>
                          {details.isApproved && (
                            <button
                              onClick={() => handleViewBreakdown(item, 'SLIP')}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 hover:bg-primary-100 text-primary-700 font-semibold text-xs rounded-lg transition-colors"
                            >
                              <Icon name="Notebook" size={13} />
                              Payslip
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Breakdown modal */}
      {showBreakdownModal && selectedEmployee && (
        <SalaryBreakdownModal
          calculatedSalaryData={selectedEmployee.calculatedSalary}
          salaryData={selectedEmployee.paySlip}
          isOpen={showBreakdownModal}
          onClose={() => {
            setShowBreakdownModal(false);
            setSelectedEmployee(null);
          }}
          employeeData={selectedEmployee.user}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          taxes={taxesData}
          refetchSalarySlip={refetchSalaries}
          initialViewMode={modalViewMode}
          userRole={currentUser?.role}
          defaultPayDate={payDate}
          isSalaryApproved={!!selectedEmployee.paySlip?.is_approved}
        />
      )}
    </div>
  );
};

export default SalaryTab;
